import { ButtonVariant } from '@vodafoneziggo/sandwich/components/button';
import { ChevronIcon, SearchClearIcon, SearchRefreshIcon } from 'assets/icons';
import { Button } from 'components/Button';
import { Loader } from 'components/Loader';
import { appSettings } from 'config/app.settings';
import { useAISearchContext } from 'context/ai-search/ai-search.context';
import { useClickOutsideAndKeyNavigation } from 'hooks/use-click-outside-and-key-navigation.hook';
import React, { FormEvent, KeyboardEvent, MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { ContentUtil } from 'utils/content';

import { AIFeedback } from './AIFeedback';
import { content } from './ai-search.content';
import * as S from './ai-search.styles';

const contentUtil = new ContentUtil({ content, componentName: 'AISearch' });

interface IAISearchProps {
  toggleSearchBar: () => void;
}

interface IAISearchResultProps {
  markdown: string;
}

const AISearchResult = ({ markdown }: IAISearchResultProps) => {
  return <Markdown aria-label="Zoekresultaten">{markdown}</Markdown>;
};

export const AISearch = ({ toggleSearchBar }: IAISearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const searchInput = useRef<HTMLInputElement>(null);
  const { aiSearchResult, aiSearchId, getAISearchResult, isLoading, hasError, resetError } = useAISearchContext();

  const ref = useClickOutsideAndKeyNavigation((_event) => {
    toggleSearchBar();
  }) as RefObject<HTMLDivElement>;

  useEffect(() => {
    searchInput?.current?.focus();
  }, [searchInput]);

  useEffect(() => {
    if (hasError || (aiSearchResult === null && submitted)) {
      setSubmitted(false);
      window.location.href = `${appSettings.ENV_ZIGGO_URL}/zoeken/?q=${encodeURI(searchQuery)}`;
      resetError();
    }
  }, [hasError, aiSearchResult, submitted]);

  const onClickShowAllResults = () => {
    if (searchQuery) {
      const description = 'laad meer resultaten clicked in smart search';
      const status = 'button_click';
      const type = 'search_results';

      triggerDdm(description, type, status, null);

      window.location.href = `${appSettings.ENV_ZIGGO_URL}/zoeken/?q=${encodeURI(searchQuery)}`;
    }
  };

  const triggerDdmButton = (interactionType: string, searchId: null | string) => {
    const description = `${interactionType} button clicked in smart search`;
    const status = 'button_click';
    const type = interactionType;

    triggerDdm(description, status, type, searchId);
  };

  const triggerDdmEnter = (searchId: null | string) => {
    const description = 'enter pressed in smart search';
    const status = 'enter_key';
    const type = 'enter';

    triggerDdm(description, status, type, searchId);
  };

  const triggerDdm = (description: string, status: string, type: string, searchId: null | string) => {
    const location = window.location.pathname;

    const payload = {
      data: {
        type: 'user_event',
        interaction: {
          event: 'smart_search',
          type,
          description,
          text: searchQuery,
          status,
          detail: searchId,
        },
        component: {
          technical_name: 'smartSearch',
          meaningful_name: 'smart_search_block',
          location,
          id: undefined,
          type: undefined,
        },
      },
    };

    window._ddm.trigger('fe.ga_event', payload);
  };

  const submit = async (eventSource: string, interactionType: string) => {
    if (!isLoading && !hasError && searchQuery) {
      try {
        const searchId = await getAISearchResult(searchQuery);
        if (eventSource === 'button_click') {
          triggerDdmButton(interactionType, searchId);
        } else {
          triggerDdmEnter(searchId);
        }
        setSubmitted(true);
      } finally {
        setSubmitted(true);
      }
    }
  };

  const onClickSubmit = (event: MouseEvent) => {
    event.preventDefault();

    submit('button_click', 'submit');
  };

  const onEnterSubmit = (event: FormEvent) => {
    event.preventDefault();

    submit('enter_key', 'submit');
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      submit('enter_key', 'submit');
    }
  };

  const onClickRefresh = (event: MouseEvent) => {
    event.preventDefault();

    submit('button_click', 'refresh');
  };

  const onClickReset = () => {
    setSearchQuery('');
    setSubmitted(false);
    searchInput?.current?.focus();

    triggerDdmButton('reset', null);
  };

  return (
    <S.SearchContainer ref={ref}>
      <S.SearchForm data-testid="ai-search-popup" onSubmit={onEnterSubmit}>
        <S.SearchInput hasResults={submitted && !hasError}>
          <S.Search
            aria-label="Zoekterm"
            ref={searchInput}
            maxLength={1024}
            data-testid="search-input"
            value={searchQuery}
            placeholder={contentUtil.translateToString('placeholder')}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={onKeyDown}
            onSubmit={onEnterSubmit}
          />
          {!isLoading && submitted && !hasError && (
            <S.ButtonIcon aria-label="Vernieuwen" type="button" onClick={onClickRefresh}>
              <SearchRefreshIcon />
            </S.ButtonIcon>
          )}
          {searchQuery && (
            <S.ButtonIcon aria-label="Wissen" type="button" onClick={onClickReset}>
              <SearchClearIcon />
            </S.ButtonIcon>
          )}
          <S.SubmitButton
            aria-label="Zoeken"
            type="submit"
            data-testid="submit-search-query"
            active={Boolean(searchQuery)}
            onClick={onClickSubmit}>
            <ChevronIcon />
          </S.SubmitButton>
        </S.SearchInput>
      </S.SearchForm>

      {submitted && !hasError && (
        <S.SearchResult>
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <AISearchResult markdown={aiSearchResult || ''} />
              <AIFeedback searchId={aiSearchId} />
              <S.Disclaimer>{contentUtil.translateToString('disclaimer')}</S.Disclaimer>
              <S.SeparationLine />
              <Button
                buttonVariant={ButtonVariant.LINK}
                testid="show-all-results-button"
                external
                isLeftAligned
                onClick={onClickShowAllResults}>
                {contentUtil.translateToString('loadMoreResults')}
              </Button>
            </>
          )}
        </S.SearchResult>
      )}
    </S.SearchContainer>
  );
};
